/**
 * Minimal minimap (feat/fog-of-war, Phase 5).
 *
 * Top-right corner canvas mirroring FoW visibility:
 *   - background = the fog state (unexplored / explored-dim / visible) read from
 *     the fog module's grids, so the minimap shows exactly what you've scouted.
 *   - entities = own + ally cubes/buildings always (the client only HAS visible
 *     enemies, so enemy dots automatically obey fog), the Wonder flagged gold.
 *   - click-to-pan: clicking jumps the camera target to that world point.
 *
 * Self-contained injected CSS (mirrors day-night-clock.ts). No external deps.
 */
import type { HelloState } from "@tribez/shared";
import type { FogApi } from "./fog";

const MAP_UNITS = 256;
const SIZE = 184; // canvas px (square)
const REDRAW_MS = 100;
const PING_RANGE = 60; // world units — "nearby" radius for expertise pings (tunable)

/** Draw a pulsing diamond ping marker centred at canvas (px, py). */
function drawPing(ctx: CanvasRenderingContext2D, px: number, py: number, color: string, pulse: number): void {
  const r = 3 * pulse;
  ctx.save();
  ctx.translate(px, py);
  ctx.rotate(Math.PI / 4);
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.85;
  ctx.fillRect(-r, -r, r * 2, r * 2);
  ctx.globalAlpha = 0.35;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.strokeRect(-r - 1.5, -r - 1.5, (r + 1.5) * 2, (r + 1.5) * 2);
  ctx.restore();
}

export interface MinimapApi {
  update(): void;
  dispose(): void;
}

export function createMinimap(opts: {
  getState: () => HelloState | null;
  getMySessionId: () => string;
  getFog: () => FogApi | null;
  onJumpTo: (x: number, z: number) => void;
  /** Camera's world footprint (4 ground corners) for the view-box, or null when
   *  the camera aims at/above the horizon. Optional (issue #5). */
  getViewBounds?: () => { x: number; z: number }[] | null;
}): MinimapApi {
  injectCss();

  const wrap = document.createElement("div");
  wrap.className = "fow-minimap";
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  wrap.appendChild(canvas);
  document.body.appendChild(wrap);
  const ctx = canvas.getContext("2d")!;

  function canvasToWorld(e: PointerEvent): { x: number; z: number } {
    const rect = canvas.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    return { x: px * MAP_UNITS, z: py * MAP_UNITS };
  }
  function jump(e: PointerEvent): void {
    const { x, z } = canvasToWorld(e);
    opts.onJumpTo(Math.max(0, Math.min(MAP_UNITS, x)), Math.max(0, Math.min(MAP_UNITS, z)));
  }
  canvas.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    jump(e);
  });
  // Native context menu over the minimap would interrupt; suppress it.
  canvas.addEventListener("contextmenu", (e) => e.preventDefault());

  let lastDraw = -1e9;

  function update(): void {
    const now = performance.now();
    if (now - lastDraw < REDRAW_MS) return;
    lastDraw = now;

    const state = opts.getState();
    if (!state) return;
    const myId = opts.getMySessionId();
    // @view()-gated maps (cubes/buildings/corpses) are undefined on the client
    // until the StateView holds a member — guard each iteration (e.g. zero
    // buildings/corpses right after a lobby→running start).
    const me = state.cubes?.get(myId);
    const myTeam = me ? me.teamId : 0;

    // --- background: fog state ---
    // The fog grid now extends past the map (origin < 0 to shroud the skirt), so
    // clip to the playable [0, MAP_UNITS] region and map cells via the grid origin.
    const fog = opts.getFog();
    if (fog) {
      const { visible, explored, cols, rows, cellSize, originX, originZ } = fog.fogState();
      ctx.fillStyle = "#0a0f19"; // base = unseen — cool dark, matches the 3D shroud FOG_COLOUR
      ctx.fillRect(0, 0, SIZE, SIZE);
      const mmS = SIZE / MAP_UNITS;
      const cpx = cellSize * mmS + 1;
      for (let cz = 0; cz < rows; cz++) {
        const wz = originZ + cz * cellSize;
        if (wz < 0 || wz >= MAP_UNITS) continue;
        for (let cx = 0; cx < cols; cx++) {
          const wx = originX + cx * cellSize;
          if (wx < 0 || wx >= MAP_UNITS) continue;
          const idx = cz * cols + cx;
          if (!visible[idx] && !explored[idx]) continue; // leave base (unseen)
          // explored = dim cool-green memory; visible = brighter live green. Hues
          // sit cooler than pure green to match the cool 3D shroud's two tiers.
          ctx.fillStyle = visible[idx] ? "#274a24" : "#152414";
          ctx.fillRect(wx * mmS, wz * mmS, cpx, cpx);
        }
      }
    } else {
      ctx.fillStyle = "#0a0f19";
      ctx.fillRect(0, 0, SIZE, SIZE);
    }

    const s = SIZE / MAP_UNITS;

    // --- buildings (squares) ---
    state.buildings?.forEach((b) => {
      if (b.state === "destroyed") return;
      const own = b.teamId === myTeam;
      ctx.fillStyle = b.buildingType === "Wonder" ? "#ffcc44" : own ? "#5cc8ff" : "#ff5a5a";
      ctx.fillRect(b.x * s - 2, b.z * s - 2, 4, 4);
    });

    // --- expertise pings (B5 Forager's Eye / GoldScout) ---
    // Resources are ungated (the client has them all), so pings are purely a
    // client read of the local player's expertise. GoldScout (Mining t5) pings
    // nearby GoldDeposits; Forager's Eye (Foraging t3) pings nearby high-value
    // gather nodes (Elder trees + herb bushes). "Nearby" = PING_RANGE from the
    // player (KTS "nearby gold deposits"); whole-map would trivialise scouting.
    if (me) {
      const goldScout = me.expertise?.mining >= 5;
      const foragersEye = me.expertise?.foraging >= 3;
      if (goldScout || foragersEye) {
        const pulse = 1 + 0.35 * Math.sin(now / 280); // gentle size oscillation
        const rangeSq = PING_RANGE * PING_RANGE;
        state.resources.forEach((r) => {
          let color: string | null = null;
          if (goldScout && r.kind === "GoldDeposit") color = "#ffd24a";
          else if (foragersEye && (r.kind === "ElderTree" || r.kind === "CulinaryHerbBush")) color = "#7df0a0";
          if (!color) return;
          const dx = r.x - me.x, dz = r.z - me.z;
          if (dx * dx + dz * dz > rangeSq) return;
          drawPing(ctx, r.x * s, r.z * s, color, pulse);
        });
      }
    }

    // --- corpses / loot drops (gold coin markers, easy to spot) ---
    // state.corpses is @view-gated server-side, so the client only holds corpses
    // its team can see — drawing them all is fog-correct.
    state.corpses?.forEach((c) => {
      const px = c.x * s, py = c.z * s;
      ctx.fillStyle = "#ffd24a";
      ctx.strokeStyle = "#2a1d00";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(px, py, 2.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    });

    // --- cubes (dots) ---
    state.cubes?.forEach((c) => {
      if (c.state === "dead") return;
      const isMe = c.sessionId === myId;
      const own = c.teamId === myTeam;
      ctx.fillStyle = c.teamId === 99 ? "#9aa0a6" : isMe ? "#ffffff" : own ? "#46e06a" : "#ff4040";
      ctx.beginPath();
      ctx.arc(c.x * s, c.z * s, isMe ? 3.2 : 2.2, 0, Math.PI * 2);
      ctx.fill();
      if (isMe) {
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    });

    // --- camera view-box (issue #5): outline where the camera is looking, so a
    // parked/scouting camera reads spatially against the hero dot. Drawn last so
    // it sits over the fog; thin + semi-transparent so it never hides entities.
    const vb = opts.getViewBounds?.();
    if (vb && vb.length === 4) {
      ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(vb[0]!.x * s, vb[0]!.z * s);
      for (let i = 1; i < vb.length; i++) ctx.lineTo(vb[i]!.x * s, vb[i]!.z * s);
      ctx.closePath();
      ctx.stroke();
    }
  }

  function dispose(): void {
    wrap.remove();
  }

  return { update, dispose };
}

let cssInjected = false;
function injectCss(): void {
  if (cssInjected) return;
  cssInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    .fow-minimap {
      position: fixed; top: 16px; right: 16px; z-index: 20;
      width: 184px; height: 184px; border-radius: 8px;
      border: 2px solid rgba(180, 200, 230, 0.35);
      box-shadow: 0 4px 14px rgba(0,0,0,0.5);
      background: #080a0e; overflow: hidden; cursor: pointer;
      user-select: none;
    }
    .fow-minimap canvas { display: block; width: 184px; height: 184px; image-rendering: auto; }
  `;
  document.head.appendChild(style);
}
